/**
 * IM WebSocket 服务
 * 处理连接管理、认证、心跳、断线重连、消息收发
 */

class IMWebSocket {
  constructor(options = {}) {
    this.url = options.url || ''
    this.ws = null
    this.state = 'DISCONNECTED' // DISCONNECTED | CONNECTING | CONNECTED | AUTHENTICATED
    this.seq = 0
    this.heartbeatTimer = null
    this.heartbeatInterval = options.heartbeatInterval || 30000
    this.reconnectTimer = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10
    this.baseReconnectDelay = options.baseReconnectDelay || 1000
    this.maxReconnectDelay = options.maxReconnectDelay || 30000
    this.manualClose = false
    this.listeners = new Map()
    this.pendingAcks = new Map() // seq -> { resolve, reject, timer }
    this.userId = null
  }

  /**
   * 建立 WebSocket 连接
   */
  connect() {
    if (this.ws && (this.state === 'CONNECTING' || this.state === 'AUTHENTICATED')) return

    this.manualClose = false
    this.state = 'CONNECTING'

    const wsUrl = this.url || process.env.VUE_APP_WS_URL || 'ws://127.0.0.1:9090/ws'
    console.log('[WS] 正在连接:', wsUrl)

    this.ws = new WebSocket(wsUrl)
    this.ws.onopen = () => this._onOpen()
    this.ws.onmessage = (event) => this._onMessage(event)
    this.ws.onclose = (event) => this._onClose(event)
    this.ws.onerror = (error) => this._onError(error)
  }

  /**
   * 主动断开连接
   */
  disconnect() {
    this.manualClose = true
    this._stopHeartbeat()
    this._clearReconnectTimer()

    // 拒绝所有待确认的消息
    this.pendingAcks.forEach(pending => {
      clearTimeout(pending.timer)
      pending.reject(new Error('WebSocket 已断开'))
    })
    this.pendingAcks.clear()

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.state = 'DISCONNECTED'
    this.userId = null
    console.log('[WS] 已断开连接')
  }

  /**
   * 发送消息帧
   * @param {string} cmd 命令类型
   * @param {object} data 业务数据
   * @returns {string} 序列号
   */
  send(cmd, data = {}) {
    const seq = this._nextSeq()
    const frame = { cmd, seq, data, timestamp: Date.now() }
    this._sendRaw(frame)
    return seq
  }

  /**
   * 发送消息并等待服务端确认
   * @param {string} cmd 命令类型
   * @param {object} data 业务数据
   * @param {number} timeout 超时毫秒数
   * @returns {Promise<{cmd, data, timestamp}>}
   */
  sendWithAck(cmd, data = {}, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const seq = this.send(cmd, data)
      const timer = setTimeout(() => {
        this.pendingAcks.delete(seq)
        reject(new Error(`消息确认超时 (seq: ${seq})`))
      }, timeout)
      this.pendingAcks.set(seq, { resolve, reject, timer })
    })
  }

  /**
   * 注册事件监听
   * @param {string} event 事件名
   * @param {Function} callback 回调
   * @returns {Function} 取消监听函数
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
    return () => this.off(event, callback)
  }

  /**
   * 移除事件监听
   */
  off(event, callback) {
    this.listeners.get(event)?.delete(callback)
  }

  // ========== 内部方法 ==========

  _onOpen() {
    this.state = 'CONNECTED'
    this.reconnectAttempts = 0
    console.log('[WS] 连接已建立，正在认证...')
    this._authenticate()
  }

  _authenticate() {
    // 从 localStorage 获取认证信息（避免循环依赖 store）
    const token = localStorage.getItem('accessToken') || ''
    const deviceId = this._getDeviceId()

    this.send('auth', {
      token,
      userType: 1,
      terminalType: 'WEB',
      deviceId
    })
  }

  _onMessage(event) {
    let frame
    try {
      frame = JSON.parse(event.data)
    } catch (e) {
      console.error('[WS] 无效的消息帧:', event.data)
      return
    }

    const { cmd, seq, data, timestamp } = frame

    // 认证结果
    if (cmd === 'auth_result') {
      if (data?.success) {
        this.state = 'AUTHENTICATED'
        this.userId = data.userId
        this._startHeartbeat()
        console.log('[WS] 认证成功, userId:', data.userId)
        this._emit('connected', data)
      } else {
        console.error('[WS] 认证失败:', data?.message)
        this._emit('auth_failed', data)
        this.disconnect()
      }
      return
    }

    // 心跳响应
    if (cmd === 'heartbeat_ack') {
      return
    }

    // 匹配待确认消息（通过 seq）
    if (seq && this.pendingAcks.has(seq)) {
      const pending = this.pendingAcks.get(seq)
      clearTimeout(pending.timer)
      this.pendingAcks.delete(seq)
      pending.resolve({ cmd, data, timestamp })
    }

    // 分发给外部监听器
    this._emit(cmd, data, timestamp)
  }

  _onClose(event) {
    this._stopHeartbeat()
    const wasAuthenticated = this.state === 'AUTHENTICATED'
    this.state = 'DISCONNECTED'

    if (!this.manualClose) {
      console.log('[WS] 连接断开，准备重连...')
      this._scheduleReconnect()
    }

    this._emit('disconnected', { wasAuthenticated, code: event.code, reason: event.reason })
  }

  _onError(error) {
    console.error('[WS] 连接错误:', error)
    this._emit('ws_error', error)
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.state === 'AUTHENTICATED') {
        this._sendRaw({ cmd: 'heartbeat', seq: this._nextSeq() })
      }
    }, this.heartbeatInterval)
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  _scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WS] 达到最大重连次数，停止重连')
      this._emit('reconnect_failed')
      return
    }

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    )
    // 添加 ±20% 抖动
    const jitter = delay * (0.8 + Math.random() * 0.4)

    console.log(`[WS] 将在 ${Math.round(jitter)}ms 后重连 (第 ${this.reconnectAttempts + 1} 次)`)

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++
      this.connect()
    }, jitter)
  }

  _clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  _nextSeq() {
    return `seq_${++this.seq}_${Date.now()}`
  }

  _getDeviceId() {
    let deviceId = localStorage.getItem('deviceId') || ''
    if (!deviceId) {
      deviceId = 'WEB-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
      localStorage.setItem('deviceId', deviceId)
    }
    return deviceId
  }

  _sendRaw(frame) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame))
    }
  }

  _emit(event, ...args) {
    this.listeners.get(event)?.forEach(cb => {
      try {
        cb(...args)
      } catch (e) {
        console.error(`[WS] 事件处理错误 (${event}):`, e)
      }
    })
  }
}

// ========== 单例管理 ==========

let instance = null

/**
 * 获取 IM WebSocket 单例
 */
export function getIMWebSocket() {
  if (!instance) {
    instance = new IMWebSocket({
      heartbeatInterval: 30000,
      maxReconnectAttempts: 10,
      baseReconnectDelay: 1000,
      maxReconnectDelay: 30000
    })
  }
  return instance
}

/**
 * 销毁 IM WebSocket 单例
 */
export function destroyIMWebSocket() {
  if (instance) {
    instance.disconnect()
    instance = null
  }
}

export default IMWebSocket
